/**
 * CodebaseAnalysisViewer — displays codebase analysis results.
 *
 * Shows file tree, language/framework badges, dependency list,
 * and uncovered functions from a CodebaseSnapshot.
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CodeIcon from '@mui/icons-material/Code';
import type { CodebaseSnapshot } from '../types';

interface CodebaseAnalysisViewerProps {
  snapshot: CodebaseSnapshot;
}

export default function CodebaseAnalysisViewer({ snapshot }: CodebaseAnalysisViewerProps) {
  const { analysis_results: analysis, file_tree: fileTree } = snapshot;

  return (
    <Paper sx={{ p: 2 }}>
      {/* Header badges */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Chip label={snapshot.language} color="primary" size="small" icon={<CodeIcon />} />
        {snapshot.framework && (
          <Chip label={snapshot.framework} color="secondary" size="small" />
        )}
        {snapshot.test_framework && (
          <Chip label={snapshot.test_framework} variant="outlined" size="small" />
        )}
        {snapshot.package_manager && (
          <Chip label={snapshot.package_manager} variant="outlined" size="small" />
        )}
        <Chip
          label={snapshot.status}
          color={snapshot.status === 'completed' ? 'success' : snapshot.status === 'failed' ? 'error' : 'default'}
          size="small"
        />
      </Box>

      {/* Summary stats */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
        <StatItem label="Files" value={fileTree.total_files} />
        <StatItem label="Routes" value={analysis.routes.length} />
        <StatItem label="Models" value={analysis.models.length} />
        <StatItem label="Functions" value={analysis.functions.length} />
        <StatItem label="Components" value={analysis.components.length} />
        <StatItem label="Test Files" value={analysis.existing_test_count} />
        <StatItem
          label="Uncovered"
          value={analysis.uncovered_functions.length}
          warn={analysis.uncovered_functions.length > 0}
        />
        <StatItem label="Indexed" value={snapshot.knowledge_entries_created} />
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Routes */}
      {analysis.routes.length > 0 && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              API Routes ({analysis.routes.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {analysis.routes.slice(0, 20).map((route, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <Chip
                    label={route.method || 'GET'}
                    size="small"
                    sx={{ mr: 1, minWidth: 60, fontFamily: 'monospace', fontSize: '0.75rem' }}
                    color={
                      route.method === 'POST' ? 'success' :
                      route.method === 'DELETE' ? 'error' :
                      route.method === 'PUT' ? 'warning' : 'default'
                    }
                  />
                  <ListItemText
                    primary={route.path}
                    secondary={route.file}
                    primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Uncovered functions */}
      {analysis.uncovered_functions.length > 0 && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" fontSize="small" />
              <Typography variant="subtitle2">
                Uncovered Functions ({analysis.uncovered_functions.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {analysis.uncovered_functions.slice(0, 20).map((func, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemText
                    primary={func.name}
                    secondary={func.file}
                    primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* File tree */}
      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            File Tree ({fileTree.total_files} files, {fileTree.dirs.length} dirs)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            {fileTree.dirs.slice(0, 30).map((dir, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}
              >
                {dir}/
              </Typography>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Dependencies */}
      {(Object.keys(snapshot.dependencies.production).length > 0 ||
        Object.keys(snapshot.dependencies.dev).length > 0) && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              Dependencies (
              {Object.keys(snapshot.dependencies.production).length} prod,{' '}
              {Object.keys(snapshot.dependencies.dev).length} dev)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Object.entries(snapshot.dependencies.production)
                .slice(0, 30)
                .map(([pkg, ver]) => (
                  <Chip
                    key={pkg}
                    label={`${pkg}@${ver}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                  />
                ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
}

function StatItem({
  label,
  value,
  warn,
}: {
  label: string;
  value: number;
  warn?: boolean;
}) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant="h6"
        sx={{ color: warn ? 'warning.main' : 'text.primary', fontWeight: 600 }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}
